import { getPresignedUploadUrl, makeRequest } from "./client";
import { uploadFileToPresignedUrl } from "../../utils/presignedUpload";
import type { DirectNoticeFile } from "../../utils/directNotices";
import type { CertificateDriveEntry } from "../../domain/drive/certificateDrive";
import type {
  CertificateTemplate,
  CertificateTemplateSettings,
} from "../../domain/certificates/certificateTemplates";
import type {
  AwardDetails,
  SavedAward,
  Certificate,
  Awardee,
  ChildCertificate,
  SentCertificateRecord,
} from "../../domain/certificates/awards";

/** A certificate template as the server stores it. */
export type ApiCertificateTemplate = Omit<CertificateTemplate, "isBuiltIn"> & {
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

/** An Award as the server stores it. */
export type ApiAward = SavedAward & {
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

/** A Certificate as the server stores it. */
export type ApiCertificate = Certificate & {
  organizationId: string;
  createdAt: string;
};

const awardPath = (awardId: string) =>
  `/v1/awards/${encodeURIComponent(awardId)}`;

const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });

export const certificatesApi = {
  /**
   * The Organisation's name and logo for certificates. A team member's login
   * doesn't carry them, so they come from the Organisation's public profile.
   */
  getOrganisation: async (
    soid: string,
  ): Promise<{ name: string; logoUrl?: string }> => {
    const organisation = await makeRequest<{ name?: string; logo?: string }>(
      `/v1/public/organizations/${encodeURIComponent(soid)}`,
      { method: "GET" },
    );
    return {
      name: organisation.name?.trim() ?? "",
      logoUrl: organisation.logo || undefined,
    };
  },

  listTemplates: async (
    token: string,
  ): Promise<{ certificateTemplates: ApiCertificateTemplate[] }> =>
    makeRequest<{ certificateTemplates: ApiCertificateTemplate[] }>(
      "/v1/certificate-templates",
      {
        method: "GET",
        headers: authHeaders(token),
      },
    ),

  getTemplate: async (
    certificateTemplateId: string,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}`,
      {
        method: "GET",
        headers: authHeaders(token),
      },
    ),

  /** Creates a draft. */
  createTemplate: async (
    settings: CertificateTemplateSettings,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      "/v1/certificate-templates",
      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(settings),
      },
    ),

  /** Replaces a draft's settings. The server refuses published ones. */
  updateTemplate: async (
    certificateTemplateId: string,
    settings: CertificateTemplateSettings,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}`,
      {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify(settings),
      },
    ),

  /** Deletes a draft. The server refuses published ones. */
  deleteTemplate: async (
    certificateTemplateId: string,
    token: string,
  ): Promise<{ message: string }> =>
    makeRequest<{ message: string }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}`,
      {
        method: "DELETE",
        headers: authHeaders(token),
      },
    ),

  /** Publishes a draft. Its design is locked from then on. */
  publishTemplate: async (
    certificateTemplateId: string,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}/publish`,
      {
        method: "POST",
        headers: authHeaders(token),
      },
    ),

  /** Archives a published one, so it's no longer offered for new Awards. */
  archiveTemplate: async (
    certificateTemplateId: string,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}/archive`,
      {
        method: "POST",
        headers: authHeaders(token),
      },
    ),

  /** Copies a saved certificate template into a new draft named "… (copy)". */
  duplicateTemplate: async (
    certificateTemplateId: string,
    token: string,
  ): Promise<{ certificateTemplate: ApiCertificateTemplate }> =>
    makeRequest<{ certificateTemplate: ApiCertificateTemplate }>(
      `/v1/certificate-templates/${encodeURIComponent(certificateTemplateId)}/duplicate`,
      {
        method: "POST",
        headers: authHeaders(token),
      },
    ),

  listAwards: async (token: string): Promise<{ awards: ApiAward[] }> =>
    makeRequest<{ awards: ApiAward[] }>("/v1/awards", {
      method: "GET",
      headers: authHeaders(token),
    }),

  /** One Award with its Certificates, oldest first. */
  getAward: async (
    awardId: string,
    token: string,
  ): Promise<{ award: ApiAward; certificates: ApiCertificate[] }> =>
    makeRequest<{ award: ApiAward; certificates: ApiCertificate[] }>(
      awardPath(awardId),
      {
        method: "GET",
        headers: authHeaders(token),
      },
    ),

  /** The server refuses draft and archived certificate templates. */
  createAward: async (
    details: AwardDetails,
    token: string,
  ): Promise<{ award: ApiAward }> =>
    makeRequest<{ award: ApiAward }>("/v1/awards", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(details),
    }),

  updateAward: async (
    awardId: string,
    details: AwardDetails,
    token: string,
  ): Promise<{ award: ApiAward }> =>
    makeRequest<{ award: ApiAward }>(awardPath(awardId), {
      method: "PUT",
      headers: authHeaders(token),
      body: JSON.stringify(details),
    }),

  /** Deletes the Award and its unsent Certificates. The server refuses once any was sent. */
  deleteAward: async (
    awardId: string,
    token: string,
  ): Promise<{ message: string }> =>
    makeRequest<{ message: string }>(awardPath(awardId), {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  /** Adds Awardees to an Award (sent as `children`). The server gives each Certificate its certificate ID. */
  addCertificates: async (
    awardId: string,
    awardees: Awardee[],
    token: string,
  ): Promise<{ certificates: ApiCertificate[] }> =>
    makeRequest<{ certificates: ApiCertificate[] }>(
      `${awardPath(awardId)}/certificates`,
      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ children: awardees }),
      },
    ),

  /**
   * Removes a Certificate. For a sent one the server first deletes every
   * notice it went out as, and only lets the Team member who added it do so.
   */
  removeCertificate: async (
    awardId: string,
    certificateId: string,
    token: string,
  ): Promise<{ message: string }> =>
    makeRequest<{ message: string }>(
      `${awardPath(awardId)}/certificates/${encodeURIComponent(certificateId)}`,
      { method: "DELETE", headers: authHeaders(token) },
    ),

  /** Records a sent Certificate's files and notices. The server refuses one already sent. */
  markCertificateSent: async (
    awardId: string,
    certificateId: string,
    sent: SentCertificateRecord,
    token: string,
  ): Promise<{ certificate: ApiCertificate }> =>
    makeRequest<{ certificate: ApiCertificate }>(
      `${awardPath(awardId)}/certificates/${encodeURIComponent(certificateId)}/sent`,
      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(sent),
      },
    ),

  /** Adds a resend's new notices to a sent Certificate, so deleting it withdraws them too. */
  recordCertificateResend: async (
    awardId: string,
    certificateId: string,
    noticeIds: string[],
    token: string,
  ): Promise<{ certificate: ApiCertificate }> =>
    makeRequest<{ certificate: ApiCertificate }>(
      `${awardPath(awardId)}/certificates/${encodeURIComponent(certificateId)}/resent`,
      {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ noticeIds }),
      },
    ),

  /** A Family child's sent certificates, newest first, found by name and parent. */
  listChildCertificates: async (
    child: Awardee,
    token: string,
  ): Promise<{ certificates: ChildCertificate[] }> => {
    const query = new URLSearchParams({
      firstName: child.firstName,
      surname: child.surname,
      parentUserIds: child.parentUserIds.join(","),
    });
    return makeRequest<{ certificates: ChildCertificate[] }>(
      `/v1/certificates/for-child?${query}`,
      { method: "GET", headers: authHeaders(token) },
    );
  },

  /** Every sent certificate, named into its Drive folder (Certificates/{award}). */
  listDriveFiles: async (
    token: string,
  ): Promise<{ certificates: CertificateDriveEntry[] }> =>
    makeRequest<{ certificates: CertificateDriveEntry[] }>(
      "/v1/certificates/drive-files",
      { method: "GET", headers: authHeaders(token) },
    ),

  /** Uploads a drawn certificate through the presigned path, ready to attach to a notice. */
  uploadCertificateFile: async (
    file: Blob,
    fileName: string,
    token: string,
  ): Promise<DirectNoticeFile> => {
    const contentType = file.type;
    const { presignedUrl, fileUrl } = await getPresignedUploadUrl(
      fileName,
      contentType,
      "certificates",
      token,
    );
    await uploadFileToPresignedUrl({ presignedUrl, file, contentType });
    return { fileUrl, fileName, contentType, size: file.size };
  },

  /** Uploads a logo through the presigned path and returns its URL. */
  uploadImage: async (file: File, token: string): Promise<string> => {
    const contentType = file.type || "image/png";
    const fileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_") || "image.png";
    const { presignedUrl, fileUrl } = await getPresignedUploadUrl(
      fileName,
      contentType,
      "certificate-templates",
      token,
    );
    await uploadFileToPresignedUrl({ presignedUrl, file, contentType });
    return fileUrl;
  },
};
